import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { useHasPermission } from '~/modules/common/permissions';
import { ChartRangeItem } from '~/modules/common/charts/timeline/components';
import { useResourceRequestToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import { deepPure } from '~/util';
import { AllocationRowLoading } from '~/modules/resourcing/common/components/ProjectChartRowsLoading/ProjectChartRowsLoading';
import { ResourceAllocationChartLeftItem } from '../ResourceAllocationChartLeftItem';
import ResourceAllocationChartTimeLineItem from '../ResourceAllocationChartTimeLineItem';
import ResourceAllocationChartFooterItem from '../ResourceAllocationChartFooterItem';
import useResourceAllocationChartActions from '../../hooks/useResourceAllocationChartActions';
import { useStyles } from './useStyles';

export const ResourceAllocationChartRow = ({
  qeIdTag,
  project,
  resourceAllocation,
  leftComponentWidth,
  isEditSwitchEnabled,
  handleAllocatedUserChange,
  handleAllocatedRoleChange,
  handleResourceChangeAndUpdateResourceAllocation,
  resourceUserActualSeries,
  createAllocationLoading,
  smartFitResourceAllocation,
  updateAllocationLoading
}) => {
  const classes = useStyles();
  const isEditAllowed =
    useHasPermission({
      actionUri: 'urn:replicon-webui:completed-resource-allocation:edit'
    }) && isEditSwitchEnabled;

  const { scale, chartDates } = useResourceRequestToolbarContext();

  const handleUserChange = useCallback(
    (user, impactedTasks) =>
      handleAllocatedUserChange({
        resourceAllocation,
        user,
        impactedTasks
      }),
    [handleAllocatedUserChange, resourceAllocation]
  );

  const handleRoleChange = useCallback(
    role => handleAllocatedRoleChange({ resourceAllocation, role }),
    [handleAllocatedRoleChange, resourceAllocation]
  );
  const [{ start: chartStartDate }] = chartDates;

  const {
    actions,
    releaseResourceAllocation,
    releaseResourceAllocationsForRequest,
    showReleaseResourcesDialog,
    closeReleaseResourcesDialog,
    releaseAllocationLoading,
    releaseAllocationsForRequestLoading
  } = useResourceAllocationChartActions({
    resourceAllocation,
    project
  });

  const leftItem = useMemo(
    () => (
      <ResourceAllocationChartLeftItem
        qeIdTag={qeIdTag}
        resourceAllocation={resourceAllocation}
        handleUserChange={handleUserChange}
        handleRoleChange={handleRoleChange}
        isEditEnabled={isEditAllowed}
        handleResourceChangeAndUpdateResourceAllocation={
          handleResourceChangeAndUpdateResourceAllocation
        }
        actions={actions}
        releaseResourceAllocation={releaseResourceAllocation}
        releaseResourceAllocationsForRequest={
          releaseResourceAllocationsForRequest
        }
        showReleaseResourcesDialog={showReleaseResourcesDialog}
        closeReleaseResourcesDialog={closeReleaseResourcesDialog}
      />
    ),
    [
      qeIdTag,
      resourceAllocation,
      handleUserChange,
      handleRoleChange,
      isEditAllowed,
      handleResourceChangeAndUpdateResourceAllocation,
      actions,
      releaseResourceAllocation,
      releaseResourceAllocationsForRequest,
      showReleaseResourcesDialog,
      closeReleaseResourcesDialog
    ]
  );

  const totalItem = useMemo(
    () => (
      <ResourceAllocationChartFooterItem
        contextCurrency={project.budgetedCost && project.budgetedCost.currency}
        resourceAllocation={resourceAllocation}
      />
    ),
    [project.budgetedCost, resourceAllocation]
  );

  const timeLineItems = useMemo(
    () => (
      <ResourceAllocationChartTimeLineItem
        isEditEnabled={isEditAllowed}
        resourceAllocation={resourceAllocation}
        resourceUserActualSeries={resourceUserActualSeries}
      />
    ),
    [isEditAllowed, resourceAllocation, resourceUserActualSeries]
  );

  if (
    releaseAllocationLoading ||
    releaseAllocationsForRequestLoading ||
    (resourceAllocation.id === smartFitResourceAllocation.id &&
      (updateAllocationLoading || createAllocationLoading))
  ) {
    return <AllocationRowLoading />;
  }

  return (
    <ChartRangeItem
      classes={classes}
      scale={scale}
      scaleItemCount={chartDates.length}
      chartStartDate={chartStartDate}
      leftComponentWidth={leftComponentWidth}
      leftComponent={leftItem}
      timeItems={timeLineItems}
      rightComponent={totalItem}
    />
  );
};

ResourceAllocationChartRow.propTypes = {
  qeIdTag: PropTypes.string,
  project: PropTypes.object.isRequired,
  resourceAllocation: PropTypes.object.isRequired,
  leftComponentWidth: PropTypes.number.isRequired,
  handleAllocatedUserChange: PropTypes.func,
  handleAllocatedRoleChange: PropTypes.func,
  isEditSwitchEnabled: PropTypes.bool,
  handleResourceChangeAndUpdateResourceAllocation: PropTypes.func,
  resourceUserActualSeries: PropTypes.object,
  createAllocationLoading: PropTypes.bool,
  smartFitResourceAllocation: PropTypes.object,
  updateAllocationLoading: PropTypes.bool
};

export default deepPure(ResourceAllocationChartRow);
